import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/cn';
import styles from './Button.module.css';

/**
 * variant: primary | dark | secondary | ghost | soft | danger | danger-ghost | link
 * size: sm | md | lg | xl
 * Renders a <Link> when `to` is set and an <a> when `href` is set.
 */
export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    block = false,
    pill = false,
    loading = false,
    iconLeft,
    iconRight,
    to,
    href,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
  },
  ref
) {
  const classes = cn(
    styles.btn,
    styles[variant],
    styles[size],
    block && styles.block,
    pill && styles.pill,
    loading && styles.loading,
    !children && styles.iconOnly,
    className
  );

  const content = (
    <>
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {iconLeft && <span className={styles.icon} aria-hidden="true">{iconLeft}</span>}
      {children != null && children !== false && <span className={styles.label}>{children}</span>}
      {iconRight && <span className={styles.icon} aria-hidden="true">{iconRight}</span>}
    </>
  );

  if (to && !disabled) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }

  if (href && !disabled) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});

/** Square/round icon-only button; `label` is required for screen readers. */
export const IconButton = forwardRef(function IconButton(
  { label, variant = 'ghost', size = 'md', round = true, className, children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(styles.iconBtn, styles[`icon-${variant}`], styles[`icon-${size}`], round && styles.round, className)}
      {...rest}
    >
      {children}
    </button>
  );
});
